import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useAreasCliente } from '../../hooks/useEmpresas'
import {
  type CriterioResultadoQai,
  type PlanoAmostragemQai,
  type PontoAmostragemQai,
  usePlanoQai,
  usePlanoQaiSalvar,
} from '../../hooks/usePlanoQai'
import { extractApiError } from '../../lib/extractApiError'
import type { PMOC } from '../../types'

const CRITERIOS = [
  { value: 'menor_que', label: 'Menor que' },
  { value: 'menor_ou_igual', label: 'Menor ou igual a (≤)' },
  { value: 'maior_que', label: 'Maior que' },
  { value: 'maior_ou_igual', label: 'Maior ou igual a (≥)' },
  { value: 'entre', label: 'Entre mínimo e máximo' },
  { value: 'igual_a', label: 'Igual a' },
  { value: 'nao_detectavel', label: 'Não detectável' },
  { value: 'apenas_registrar', label: 'Apenas registrar' },
]

export default function PmocPlanoQaiTab({ pmoc }: { pmoc: PMOC }) {
  const consulta = usePlanoQai(pmoc.id)
  const salvar = usePlanoQaiSalvar(pmoc.id)
  const areas = useAreasCliente(pmoc.cliente ?? '')
  const [form, setForm] = useState<PlanoAmostragemQai | null>(null)

  useEffect(() => {
    if (consulta.data) setForm(consulta.data)
  }, [consulta.data])

  if (consulta.isLoading || !form) return <Text c="dimmed">Carregando plano QAI...</Text>

  const plano = form
  const editavel = pmoc.status === 'rascunho'
  const areasOpcoes = (areas.data?.results ?? []).map((area) => ({
    value: area.id,
    label: `${area.nome}${area.codigo ? ` (${area.codigo})` : ''}`,
  }))
  const internos = form.pontos.filter((ponto) => ponto.tipo === 'interno').length
  const externos = form.pontos.filter((ponto) => ponto.tipo === 'externo_referencia').length
  const dimensionamento = form.dimensionamento_nbr

  function atualizarPonto(indice: number, dados: Partial<PontoAmostragemQai>) {
    setForm({
      ...plano,
      pontos: plano.pontos.map((ponto, i) => (i === indice ? { ...ponto, ...dados } : ponto)),
    })
  }

  function completarPontos() {
    const faltam = Math.max(0, plano.quantidade_planejada - internos)
    const areasSugeridas = dimensionamento.areas
    const novos = Array.from({ length: faltam }, (_, indice) => ({
      area:
        areasSugeridas[(internos + indice) % Math.max(areasSugeridas.length, 1)]?.id ??
        areasOpcoes[0]?.value ??
        null,
      identificacao: `Ponto interno ${internos + indice + 1}`,
      tipo: 'interno' as const,
      local_coleta: '',
      ordem: plano.pontos.length + indice + 1,
    }))
    setForm({ ...plano, pontos: [...plano.pontos, ...novos] })
  }

  function aplicarDimensionamentoNBR() {
    const minimo = dimensionamento.quantidade_minima
    if (!minimo || dimensionamento.areas.length === 0) return
    const outrosPontos = plano.pontos.filter((ponto) => ponto.tipo !== 'interno')
    const internosSugeridos = Array.from({ length: minimo }, (_, indice) => {
      const area = dimensionamento.areas[indice % dimensionamento.areas.length]
      return {
        area: area.id,
        identificacao: `Ponto interno ${indice + 1}`,
        tipo: 'interno' as const,
        local_coleta: 'Centro do ambiente, a 1,5 m do piso',
        ordem: indice + 1,
      }
    })
    const referenciaExterna =
      outrosPontos.length > 0
        ? outrosPontos
        : [
            {
              area: null,
              identificacao: 'Referência externa 1',
              tipo: 'externo_referencia' as const,
              local_coleta: 'Área externa representativa',
              ordem: minimo + 1,
            },
          ]
    setForm({
      ...plano,
      quantidade_minima: minimo,
      quantidade_planejada: minimo,
      quantidade_maxima:
        plano.quantidade_maxima !== null && plano.quantidade_maxima < minimo
          ? minimo
          : plano.quantidade_maxima,
      criterio_dimensionamento:
        `Tabela 3 da ABNT NBR 17037:2023: ${minimo} amostra(s) interna(s) ` +
        `para ${dimensionamento.area_climatizada_m2} m² de área climatizada, ` +
        'mais uma amostra de ar exterior no mesmo período.',
      pontos: [...internosSugeridos, ...referenciaExterna],
    })
  }

  function atualizarCriterio(indice: number, dados: Partial<CriterioResultadoQai>) {
    setForm({
      ...plano,
      criterios: plano.criterios.map((criterio, i) =>
        i === indice ? { ...criterio, ...dados } : criterio,
      ),
    })
  }

  function carregarCriteriosNBR() {
    if (
      plano.criterios.length > 0 &&
      !confirm(
        'Substituir os parâmetros atuais pelos padrões referenciais da ABNT NBR 17037:2023?',
      )
    ) {
      return
    }
    setForm({
      ...plano,
      criterios: plano.criterios_padrao_nbr.map((criterio) => ({ ...criterio })),
    })
  }

  async function handleSalvar() {
    try {
      const salvo = await salvar.mutateAsync(plano)
      setForm(salvo)
      notifications.show({ color: 'green', message: 'Plano de amostragem QAI salvo.' })
    } catch (erro) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível salvar o plano QAI',
        message: extractApiError(erro, 'Confira quantidades, pontos e critérios.'),
      })
    }
  }

  return (
    <Stack>
      <Alert color={form.configurado ? 'blue' : 'orange'} title="Obrigatório antes da ativação">
        Defina o plano de amostragem agora. Laboratório e RT da análise podem ser escolhidos
        depois, sem perder o compromisso do cliente com as coletas.
      </Alert>

      <Paper withBorder p="lg">
        <Title order={4} mb="md">Dimensionamento</Title>
        {dimensionamento.quantidade_minima !== null ? (
          <Alert color="green" mb="md" title="Dimensionamento calculado pela NBR 17037">
            Área climatizada dos equipamentos deste PMOC: {dimensionamento.area_climatizada_m2} m².
            Mínimo: {dimensionamento.quantidade_minima} amostra(s) interna(s), além de uma
            referência externa.
            {editavel && (
              <Group mt="sm">
                <Button size="xs" onClick={aplicarDimensionamentoNBR}>
                  Aplicar mínimo e sugerir pontos
                </Button>
              </Group>
            )}
          </Alert>
        ) : dimensionamento.areas_sem_metragem.length > 0 ? (
          <Alert color="orange" mb="md" title="Falta a metragem dos ambientes">
            O sistema importou os ambientes, mas precisa da área em m² para calcular o mínimo:
            {' '}{dimensionamento.areas_sem_metragem.map((area) => area.nome).join(', ')}.
          </Alert>
        ) : (
          <Alert color="orange" mb="md" title="Nenhum ambiente encontrado neste PMOC">
            Inclua os equipamentos no PMOC. Os ambientes vêm da coluna LOCAL/AMBIENTE da
            planilha de equipamentos.
          </Alert>
        )}
        <Group grow align="start">
          <NumberInput label="Periodicidade (meses)" min={1} value={form.periodicidade_meses} disabled={!editavel} onChange={(v) => setForm({ ...form, periodicidade_meses: Number(v) })} />
          <NumberInput label="Quantidade mínima" min={1} value={form.quantidade_minima} disabled={!editavel} onChange={(v) => setForm({ ...form, quantidade_minima: Number(v) })} />
          <NumberInput label="Quantidade máxima" min={1} value={form.quantidade_maxima ?? ''} disabled={!editavel} onChange={(v) => setForm({ ...form, quantidade_maxima: v === '' ? null : Number(v) })} />
          <NumberInput label="Quantidade planejada" min={1} value={form.quantidade_planejada} disabled={!editavel} onChange={(v) => setForm({ ...form, quantidade_planejada: Number(v) })} />
        </Group>
        <Textarea mt="md" required label="Critério de dimensionamento" description="Explique a base técnica usada para definir a quantidade e distribuir os pontos." value={form.criterio_dimensionamento} disabled={!editavel} onChange={(e) => setForm({ ...form, criterio_dimensionamento: e.currentTarget.value })} />
        <TextInput mt="md" label="Referência normativa" value={form.referencia_normativa} disabled={!editavel} onChange={(e) => setForm({ ...form, referencia_normativa: e.currentTarget.value })} />
      </Paper>

      <Paper withBorder p="lg">
        <Group justify="space-between" mb="md">
          <div><Title order={4}>Ambientes e pontos</Title><Text size="sm" c={internos === form.quantidade_planejada && externos > 0 ? 'green' : 'red'}>{internos} de {form.quantidade_planejada} pontos internos · {externos} referência(s) externa(s)</Text></div>
          {editavel && <Group><Button variant="light" onClick={completarPontos}>Criar pontos faltantes</Button><Button variant="default" onClick={() => setForm({ ...form, pontos: [...form.pontos, { area: null, identificacao: `Referência externa ${form.pontos.length + 1}`, tipo: 'externo_referencia', local_coleta: '', ordem: form.pontos.length + 1 }] })}>+ Referência externa</Button></Group>}
        </Group>
        <Stack>
          {form.pontos.map((ponto, indice) => (
            <Group key={ponto.id ?? indice} align="end" wrap="nowrap">
              <TextInput label="Identificação" value={ponto.identificacao} disabled={!editavel} onChange={(e) => atualizarPonto(indice, { identificacao: e.currentTarget.value })} />
              <Select label="Tipo" data={[{ value: 'interno', label: 'Interno' }, { value: 'externo_referencia', label: 'Externo' }]} value={ponto.tipo} disabled={!editavel} onChange={(v) => v && atualizarPonto(indice, { tipo: v as PontoAmostragemQai['tipo'], area: v === 'interno' ? ponto.area : null })} />
              <Select searchable clearable label="Área" data={areasOpcoes} value={ponto.area} disabled={!editavel || ponto.tipo !== 'interno'} onChange={(v) => atualizarPonto(indice, { area: v })} />
              <TextInput label="Posição/local da coleta" value={ponto.local_coleta} disabled={!editavel} onChange={(e) => atualizarPonto(indice, { local_coleta: e.currentTarget.value })} />
              {editavel && <Button color="red" variant="subtle" onClick={() => setForm({ ...form, pontos: form.pontos.filter((_, i) => i !== indice) })}>Remover</Button>}
            </Group>
          ))}
        </Stack>
      </Paper>

      <Paper withBorder p="lg">
        <Group justify="space-between" mb="md">
          <div><Title order={4}>Parâmetros e resultados esperados</Title><Text size="sm" c="dimmed">Os padrões abaixo vêm da Seção 5 da ABNT NBR 17037:2023. Parâmetros adicionais dependem das fontes de risco e do tipo de ocupação.</Text></div>
          {editavel && <Group><Button variant="light" onClick={carregarCriteriosNBR}>Carregar padrões da NBR</Button><Button onClick={() => setForm({ ...form, criterios: [...form.criterios, { parametro: '', unidade: '', criterio: 'menor_que', valor_min: null, valor_max: null, valor_referencia: null, obrigatorio: true, observacoes: '', ordem: form.criterios.length + 1 }] })}>+ Parâmetro adicional</Button></Group>}
        </Group>
        <Alert color="blue" mb="md" title="Como interpretar">
          CO₂ e fungos dependem também da amostra externa. Bactérias mesófilas ficam
          desmarcadas por padrão porque sua exigência depende do perfil epidemiológico do
          ambiente. A faixa de umidade é recomendável, não um limite absoluto de segurança.
        </Alert>
        <Table.ScrollContainer minWidth={1350}>
          <Table>
            <Table.Thead><Table.Tr><Table.Th>Parâmetro</Table.Th><Table.Th>Unidade</Table.Th><Table.Th>Critério</Table.Th><Table.Th>Mínimo</Table.Th><Table.Th>Máximo</Table.Th><Table.Th>Referência</Table.Th><Table.Th>Incluir</Table.Th><Table.Th>Observação técnica</Table.Th><Table.Th /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {form.criterios.map((criterio, indice) => (
                <Table.Tr key={criterio.id ?? indice}>
                  <Table.Td><TextInput value={criterio.parametro} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { parametro: e.currentTarget.value })} /></Table.Td>
                  <Table.Td><TextInput value={criterio.unidade} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { unidade: e.currentTarget.value })} /></Table.Td>
                  <Table.Td><Select data={CRITERIOS} value={criterio.criterio} disabled={!editavel} onChange={(v) => v && atualizarCriterio(indice, { criterio: v as CriterioResultadoQai['criterio'] })} /></Table.Td>
                  <Table.Td><TextInput type="number" value={criterio.valor_min ?? ''} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { valor_min: e.currentTarget.value || null })} /></Table.Td>
                  <Table.Td><TextInput type="number" value={criterio.valor_max ?? ''} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { valor_max: e.currentTarget.value || null })} /></Table.Td>
                  <Table.Td><TextInput type="number" value={criterio.valor_referencia ?? ''} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { valor_referencia: e.currentTarget.value || null })} /></Table.Td>
                  <Table.Td><Checkbox checked={criterio.obrigatorio} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { obrigatorio: e.currentTarget.checked })} /></Table.Td>
                  <Table.Td><Textarea autosize minRows={1} maxRows={4} value={criterio.observacoes} disabled={!editavel} onChange={(e) => atualizarCriterio(indice, { observacoes: e.currentTarget.value })} /></Table.Td>
                  <Table.Td>{editavel && <Button color="red" variant="subtle" onClick={() => setForm({ ...form, criterios: form.criterios.filter((_, i) => i !== indice) })}>Remover</Button>}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Textarea label="Observações do plano QAI" value={form.observacoes} disabled={!editavel} onChange={(e) => setForm({ ...form, observacoes: e.currentTarget.value })} />
      {editavel && <Group justify="flex-end"><Button loading={salvar.isPending} onClick={handleSalvar}>Salvar plano QAI</Button></Group>}
    </Stack>
  )
}
